import React from "react";

import NoDataMessage from "../../../common/NoDataMessage";
import { PlatformSubscriptionHistory } from "../../../common/platformTypes";
import { BoolCheck } from "../../../components/BoolCheck";
import { Date as DateView } from "../../../components/Date";
import {
  DocumentTable,
  DocumentTableProperty,
} from "../../../components/DocumentTable";

type HistoryRow = PlatformSubscriptionHistory & { _id: string; kind: string };

const PROPERTIES: DocumentTableProperty<HistoryRow>[] = [
  { title: "Kind", render: (d) => d.kind },
  { title: "Tier", render: (d) => d.tier },
  { title: "Trial", render: (d) => <BoolCheck check={d.isTrial} /> },
  {
    title: "Started",
    render: (d) => <DateView date={new Date(d.startDate)} />,
  },
  { title: "Ended", render: (d) => <DateView date={new Date(d.endDate)} /> },
  {
    title: "Memo",
    render: (d) => d.memo ?? <span className="text-muted">—</span>,
  },
];

export const HistoryTab: React.FunctionComponent<{
  trials: PlatformSubscriptionHistory[];
  prior: PlatformSubscriptionHistory[];
}> = ({ trials, prior }) => {
  const rows: HistoryRow[] = [
    ...trials.map((t, i) => ({ ...t, kind: "Trial", _id: `trial-${i}` })),
    ...prior.map((p, i) => ({ ...p, kind: "Plan", _id: `prior-${i}` })),
  ].sort(
    (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime(),
  );

  if (!rows.length) {
    return <NoDataMessage light componentName="history" />;
  }

  const lastTrialEnd = trials.length
    ? new Date(Math.max(...trials.map((t) => new Date(t.endDate).getTime())))
    : undefined;
  // Six months from the last trial's end is when the next one becomes
  // available — the rule the self-serve path enforces and an admin override
  // silently bypasses.
  const cooldownEnds = lastTrialEnd
    ? new Date(new Date(lastTrialEnd).setMonth(lastTrialEnd.getMonth() + 6))
    : undefined;

  return (
    <>
      {cooldownEnds && cooldownEnds > new Date() && (
        <div className="alert alert-warning">
          Last trial ended <DateView date={lastTrialEnd} />. Not eligible for
          another until <DateView date={cooldownEnds} /> — granting one now
          overrides the six-month cooldown.
        </div>
      )}
      <DocumentTable<HistoryRow> light objects={rows} properties={PROPERTIES} />
    </>
  );
};
